import React, { useState } from 'react';
import { Button, Modal, Form, Input, InputNumber, DatePicker, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import SelectSearchInput from '../../utils/components/selectSearchInput';
import dayjs from 'dayjs';
import axios from '../../services/axios';

const DEVISE_GNF = 'GNF';

const formatGNF = (value: any) =>
  Math.round(Number(value) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const thousands = (value: any) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const unThousands = (value: any) => `${value}`.replace(/\s/g, '');

interface CollectionCreateFormProps {
  visible: boolean;
  onCreate: (values: any) => Promise<void>;
  onCancel: () => void;
  clients: any[];
  setClientVisible: () => void;
}

const CollectionCreateForms: React.FC<CollectionCreateFormProps> = ({
  visible,
  onCreate,
  onCancel,
  clients,
  setClientVisible,
}) => {
  const [form] = Form.useForm();
  const [searchingDepot, setSearchingDepot] = useState(false);
  const [depotFound, setDepotFound] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [montantRestant, setMontantRestant] = useState(0);
  const [depotInfo, setDepotInfo] = useState<any>(null);

  const reset = () => {
    form.resetFields();
    setDepotFound(false);
    setFormKey(0);
    setMontantRestant(0);
    setDepotInfo(null);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  // Le rattachement à un dépôt est facultatif; s'il est fait, il plafonne le
  // montant retirable au reste du dépôt.
  const searchDepotByNumero = () => {
    const numeroRecu = form.getFieldValue('numero_recu');

    if (!numeroRecu) {
      message.warning('Veuillez saisir un numéro de reçu');
      return;
    }

    setSearchingDepot(true);

    axios.get(`/operations/search/numero/${numeroRecu}`)
      .then((response) => {
        const depot = response.data;

        if (depot && depot.type_operation === 'depot') {
          const montantDisponible = depot.montant_restant || depot.montant_sortie;

          setDepotInfo(depot);
          setMontantRestant(montantDisponible);

          form.setFieldsValue({
            'client_id': depot.client_id,
            'montant': montantDisponible,
            'pays_depart': depot.pays_depart || 'LABE',
            'pays_recuperation': depot.pays_recuperation || 'CONAKRY',
            'motif': depot.motif || 'achat',
            'type_paiement': depot.type_paiement || 'espece',
            'nom_deposant': depot.nom_deposant,
            'tel_deposant': depot.tel_deposant,
          });

          setDepotFound(true);
          setFormKey(prev => prev + 1);
          message.success(
            `Dépôt trouvé — reste ${formatGNF(montantDisponible)} ${depot.devisesortie?.code}`
          );
        } else {
          message.error('Aucun dépôt trouvé avec ce numéro de reçu');
          setDepotFound(false);
          setMontantRestant(0);
          setDepotInfo(null);
        }

        setSearchingDepot(false);
      })
      .catch((error) => {
        console.log(error);
        const errorData = error.response?.data;

        if (error.response?.status === 400 && errorData?.fully_withdrawn) {
          message.error({
            content: `Ce dépôt a été entièrement retiré ! Montant initial: ${errorData.depot_initial}, Total retiré: ${errorData.total_retire}`,
            duration: 6,
          });
        } else if (typeof errorData === 'string') {
          message.error(errorData);
        } else {
          message.error('Erreur lors de la recherche du dépôt');
        }

        setSearchingDepot(false);
        setDepotFound(false);
        setMontantRestant(0);
        setDepotInfo(null);
      });
  };

  const validateMontant = (_: any, value: number) => {
    if (!value) {
      return Promise.reject('Le montant est requis');
    }
    if (value <= 0) {
      return Promise.reject('Le montant doit être supérieur à 0');
    }
    if (depotFound && value > montantRestant) {
      return Promise.reject(`Le montant ne peut pas dépasser ${formatGNF(montantRestant)}`);
    }
    return Promise.resolve();
  };

  return (
    <Modal
      open={visible}
      width={640}
      title={
        <>
          Nouvelle sortie d'argent
          <Button onClick={() => setClientVisible()} type="dashed" style={{ marginLeft: 8 }}>
            Nouveau Client
          </Button>
        </>
      }
      okText="Enregistrer"
      cancelText="Annuler"
      onCancel={handleCancel}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            onCreate({ ...values, devise_id: DEVISE_GNF, devise_sortie: DEVISE_GNF })
              .then(reset)
              .catch(() => {});
          })
          .catch(info => console.log('Validate Failed:', info));
      }}
    >
      <Form
        key={formKey}
        form={form}
        layout="horizontal"
        labelCol={{ span: 7 }}
        name="form_sortie"
        initialValues={{
          type_paiement: 'espece',
          motif: 'achat',
          taux: 1,
          date_operation: dayjs(),
          pays_depart: 'LABE',
          pays_recuperation: 'CONAKRY',
        }}
      >
        <Form.Item label="N° reçu dépôt" tooltip="Facultatif. Renseigné, il plafonne le montant au reste du dépôt.">
          <Input.Group compact>
            <Form.Item name="numero_recu" noStyle>
              <Input
                style={{ width: 'calc(100% - 40px)' }}
                placeholder="N° de reçu du dépôt (facultatif)"
              />
            </Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={searchingDepot}
              onClick={searchDepotByNumero}
            />
          </Input.Group>
        </Form.Item>

        {depotFound && depotInfo && (
          <div style={{
            padding: 10,
            backgroundColor: '#e6f7ff',
            borderRadius: 4,
            marginBottom: 16,
            border: '1px solid #91d5ff',
          }}>
            <strong>Retrait sur dépôt</strong>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Montant initial :</span>
              <strong>{formatGNF(depotInfo.montant_du ?? depotInfo.montant_sortie)} {depotInfo.devisesortie?.code}</strong>
            </div>
            {depotInfo.nombre_retraits > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Déjà retiré :</span>
                <strong style={{ color: 'red' }}>{formatGNF(depotInfo.total_retire)} {depotInfo.devisesortie?.code}</strong>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Reste :</span>
              <strong style={{ color: 'green' }}>{formatGNF(montantRestant)} {depotInfo.devisesortie?.code}</strong>
            </div>
          </div>
        )}

        <Form.Item label="Date de sortie" name="date_operation" rules={[{ required: true }]}>
          <DatePicker format="YYYY-MM-DD" placeholder="date" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
          <SelectSearchInput
            onChange={(v) => form.setFieldsValue({ client_id: v })}
            data={clients}
            label="prenom"
            label2="nom"
            label3="telephone"
            valueLabel="id"
            placeholder="Sélectionnez le compte"
            disabled={depotFound}
          />
        </Form.Item>

        <Form.Item name="pays_depart" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="pays_recuperation" noStyle>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item label="Déposant" style={{ marginBottom: 0 }}>
          <Form.Item name="nom_deposant" rules={[{ required: true, message: 'Nom du déposant requis' }]}
            style={{ display: 'inline-block', width: 'calc(60% - 8px)' }}>
            <Input placeholder="nom du déposant" disabled={depotFound} />
          </Form.Item>
          <Form.Item name="tel_deposant" style={{ display: 'inline-block', width: 'calc(40% - 8px)', margin: '0 8px' }}>
            <Input placeholder="téléphone" disabled={depotFound} />
          </Form.Item>
        </Form.Item>

        <Form.Item name="beneficiaire" label="Bénéficiaire" rules={[{ required: true, message: 'Bénéficiaire requis' }]}>
          <Input placeholder="nom du bénéficiaire" />
        </Form.Item>

        <Form.Item name="montant" label="Montant" rules={[{ validator: validateMontant }]}>
          <InputNumber
            min={0}
            placeholder="Montant en GNF"
            style={{ width: '100%' }}
            addonAfter="GNF"
            formatter={thousands}
            parser={unThousands as any}
          />
        </Form.Item>

        <Form.Item initialValue="retrait" name="type_operation" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="taux" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="motif" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="type_paiement" noStyle>
          <Input type="hidden" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CollectionCreateForms;
